package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/basic")
@Composable
fun BasicCoroutinesPage() {
    BlogLayout(
        PageProperties(
            "Coroutines",
            "Coroutines execute long-running tasks without blocking the main thread or freezing the UI."
        ),
        BlogProperties("Coroutines", "Basic")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Why Coroutines?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handling concurrency in Android using older methods like AsyncTask or threading can be difficult and prone to errors. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines offer a simpler way to handle concurrent tasks. They allow you to run code concurrently (in the background) and suspend code execution without blocking the main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike threads, which are heavy and can’t be paused and resumed easily, coroutines can suspend their work and resume later, even on a different thread, without complicating your code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Concurrency vs. Parallelism on Dispatchers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s a common myth that async always means tasks run at the exact same physical moment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On Dispatchers.Main: Coroutines interleave during suspension points (like network delays), but actual CPU work is single-threaded. One coroutine pauses so another can start.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On Dispatchers.Default/IO: These use thread pools where true parallelism (running on different CPU cores) actually happens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Concurrency means that multiple tasks make progress simultaneously. This doesn't necessarily mean they are running at the exact same time (which would be parallelism), but rather that their executions overlap. This allows efficient use of resources and keeps applications responsive by preventing the main thread from being blocked by long-running operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While coroutines are cheap, the resources they hit (APIs, databases) are not. Launching 1,000 async blocks at once can overwhelm your system. Use a Semaphore to bound your work:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val semaphore = Semaphore(10) // Limit to 10 concurrent requests

                    val deferreds = items.map { item ->
                        async {
                            semaphore.withPermit { api.fetchData(item) }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Multithreading and concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multithreading allows programs to execute multiple threads concurrently, enabling parallelism and responsiveness.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Concurrency, on the other hand, refers to the ability of tasks to make progress independently of each other, enhancing the overall throughput and efficiency of the system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Mutex",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mutex is to coroutines, what synchronised function is to the threads. One cannot lock coroutine on any object, like with a thread. But you can lock coroutine on a Mutex.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a coroutine locks itself on a Mutex, then every other coroutine, which tries to lock onto the same Mutex instance, will get suspended untill the original coroutine finishes whatever it was doing inside the locked section. Exactly like a synchronised block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Semaphore",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Controls access to a resource pool with a fixed number of permits. A Semaphore is a concurrency control mechanism that restricts the number of coroutines accessing a shared resource at the same time. It works by maintaining a permit count, which determines how many coroutines can execute a given operation concurrently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can use Semaphores in:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Limiting API calls to prevent exceeding rate limits.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Controlling database writes to avoid deadlocks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Managing access to a shared file in multi-threaded environments.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                     val dispatcher = Dispatchers.Default.limitedParallelism(parallelism = 2)
                    """.trimIndent()
            )

            SpanText(
                text = "Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Non-blocking: Allow launching multiple coroutines simultaneously.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lightweight: Consume fewer resources compared to threads.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structured Concurrency: Encourage a more structured approach to writing asynchronous code. Structured concurrency ensures that coroutines are launched in a controlled hierarchy, so you don’t end up with untracked, leaking coroutines. “Structured concurrency ensures that when a coroutine scope finishes, all coroutines within that scope are also completed. Builders like launch and async ensure that coroutines have clear lifecycles and are properly canceled if needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines Create: Coroutines are created using suspend functions and can be launched using launch or async builders from the kotlinx.coroutines library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines execute long-running tasks without blocking the main thread or freezing the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine is a lightweight thread that can be used for performing asynchronous tasks without blocking the main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines are built on top of threads but offer a simpler and more efficient way to write asynchronous code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines allow developers to write asynchronous code sequentially, without the complexity of nested callbacks or explicit thread management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you launch a coroutine, it runs in the background and can perform operations such as I/O or long-running computations. Meanwhile, the main thread is free to continue executing other tasks, such as updating the UI or handling user input.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines in Kotlin are implemented using suspend functions, which are functions that can be paused and resumed later without blocking the thread. When a coroutine encounters a suspend function, it can suspend its execution and allow other coroutines to run. Once the suspend function completes, the coroutine can resume its execution from where it left off.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Asynchronous",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous programming allows tasks to run independently of the main program flow. (Code runs in different threads or coroutines, avoiding blocking the main thread, which is ideal for handling time-consuming tasks.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s useful for time-consuming operations like network requests, file I/O, or user input handling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of blocking the program, asynchronous programming lets it continue executing other tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Upon completion, asynchronous operations notify the program to process results or take further actions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Synchronous",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Code is executed sequentially, line by line. While easy to understand and write, it can lead to UI freezes or ANR (Application Not Responding) issues when tasks take too long, such as downloading data from the internet. Running this task on the main thread would freeze the UI, causing a poor user experience.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tasks are done one at a time, in order. If one task takes a long time, the whole program seems frozen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Understanding the Concurrency Model",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A thread operates at the system level, with each thread often tied to a single CPU core. Threads work in true parallelism, where multiple threads execute at the same time, each performing part of the workload concurrently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example: a multi-threaded application might dedicate one thread to handling network requests and another to database operations, all running simultaneously (we assumes that there’s enough cores available).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On the other hand, coroutines can operate within a single thread by using cooperative multitasking. Rather than being managed by the OS, coroutines control their execution flow. A coroutine runs until it explicitly suspends itself, allowing another coroutine to take over the thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example: coroutine A might execute for 2 milliseconds, then suspend its execution while coroutine B runs for 3 milliseconds. Coroutine A can then resume its work after coroutine B finishes or suspends itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This makes coroutines lightweight and efficient, enabling thousands of coroutines to operate on a smaller number of threads. By using a shared pool of threads, coroutines optimize resource utilization compared to a thread-only approach, which is constrained by system resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Structured Concurrency Vs Unstructured concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Structured Concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Structured concurrency ensures that all coroutines are launched within a defined scope and follow a parent-child hierarchy. This guarantees proper lifecycle management, cancellation propagation, and predictable execution.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In production, structured concurrency is the recommended approach because it prevents memory leaks, orphan tasks, and inconsistent states. It ensures that all work is completed or cancelled in a controlled manner.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoid using GlobalScope for request-scoped work. It bypasses Structured Concurrency, meaning if the user navigates away or the request is canceled, the GlobalScope work will leak and continue to run in the background. Always use coroutineScope { ... } to ensure child tasks are tied to the caller's lifecycle.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin coroutines enforce structured concurrency by default through CoroutineScope and Job hierarchy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Unstructured concurrency, on the other hand, allows coroutines to be launched without a clear scope or lifecycle. This often leads to “fire-and-forget” tasks that are difficult to manage.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Unstructured concurrency may seem simpler initially but introduces significant risks in large systems. Debugging issues becomes difficult because there is no clear ownership of coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structured Coroutines Plugin: https://plugins.jetbrains.com/plugin/30009-structured-coroutines",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines form a tree of parents and children. A CoroutineScope defines the lifetimes of its children:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the scope is canceled, its children are also canceled.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A suspend function that uses coroutineScope { ... } waits for all its child tasks to finish before returning.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Embrace structured concurrency by using coroutine scopes and structured concurrency patterns to ensure that all launched coroutines complete before the parent scope completes. Structured Concurrency ensures that coroutines have a parent-child relationship. A parent coroutine is responsible for its children; if the parent cancels, all children cancel. If a child fails, the parent is notified and can handle the exception.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It ties the lifecycle of coroutines to a specific Scope, such as an activity or view model, ensuring that coroutines are canceled when the scope is no longer active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structured concurrency helps in managing the lifecycle of coroutines, preventing issues like dangling references and resource leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines in Kotlin follow structured concurrency, meaning they automatically manage cancellation and exception handling within a defined scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Advantage of Structured Concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Resource Management: Prevent leaks by ensuring all resources released when the parent scope is completed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Error Handling: Any errors in the code are reported to the parent coroutine and never lost.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatically Lifecycle Management: The scope can automatically cancel child coroutines if something goes wrong or a user changes their mind and decides to revoke the operation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun main() = runBlocking {
                        val topScope = CoroutineScope(Job())
                        val parentJob = topScope.launch {

                            // Launching child coroutines within the parentJob
                            val job1 = launch {
                                try {
                                    delay(1000L)
                                    println("Task from job1")
                                } finally {
                                    println("Job1 is cancelled")
                                }
                            }
                            val job2 = launch {
                                try {
                                    delay(2000L)
                                    println("Task from job2")
                                } finally {
                                    println("Job2 is cancelled")
                                }
                            }
                        }

                        // Wait for some time before cancelling the parent job
                        delay(1500L)
                        println("Cancelling parent job")
                        parentJob.cancelAndJoin()
                        println("Parent job is cancelled")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Why Use Coroutines?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If we run long tasks (like fetching data from the internet) on the main thread, the app will “freeze,” causing a poor user experience. Coroutines solve this problem by allowing us to keep these tasks off the main thread while still keeping the code simple and readable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Pros of Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines have launched asynchronously. They allow developers to execute long-running tasks, such as network requests, database operations, or file I/O, without blocking the main thread. This helps maintain a responsive user interface and prevents ANR (Application Not Responding) errors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines enable developers to write asynchronous code in a sequential manner, similar to synchronous code. This leads to cleaner, more readable, and maintainable code compared to traditional callback-based or nested asynchronous code structures. Coroutines use language features like suspending functions and structured concurrency to achieve this clarity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines suspend the caller thread (don’t block the caller thread).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike threads, which are heavyweight operating system resources, coroutines are lightweight and can be launched by the hundreds or thousands without significant overhead. This makes coroutines more efficient in terms of resource usage, especially on mobile devices with limited resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines support structured concurrency, which helps in organizing and managing concurrent tasks in a hierarchical manner. This ensures that coroutines are properly scoped and managed, preventing resource leaks and simplifying error handling and cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A single coroutine can use multiple dispatchers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines allow context switching, which means it can be switched between different threads, such as the IO thread and the main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspend functions has to be invoked from the coroutine or from another suspend function. (Improving performance by reducing thread overhead and context switching.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple coroutines can be associated with a coroutine scope at any given time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each coroutine is associated with a job, which provides a unique identifier that allows it to be tracked and canceled if necessary.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines can return a value, which means that structured concurrency can be achieved without the need for callbacks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enabling advanced features such as cancellation, timeouts, error handling, and testing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Debugging coroutines can be tricky, especially when dealing with multiple scopes and contexts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Testing coroutines can be challenging, especially when using delay or withTimeout functions that depend on time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handling errors in coroutines can be complex, especially when using async or launch builders that do not propagate exceptions by default.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using coroutine scopes to control the lifetime and cancellation of your coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using channels and flows to communicate between your coroutines in a reactive and declarative way.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why we need to create ViewModel object with ViewModelProvider class",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It will return an existing viewmodel instance if already exists, other wise create new instance and return it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It will create the ViewModel instance with the given Scope (Activity/Fragment)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Instance is alive as long as the current scope is alive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "CoroutineStart Modes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "CoroutineStart is a CoroutineContext element. It determines how and when a coroutine is executed.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DEFAULT: The go-to choice! It schedules the coroutine for immediate execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LAZY: For a strategic delay! The coroutine starts only when its result is required, like calling start or await/join.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ATOMIC: Immediate but unstoppable! Similar to DEFAULT but cannot be cancelled before it starts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UNDISPATCHED: Quick execution in the current thread, until the first suspension point.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Routines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "This explanation dives into routines and coroutines, exploring their differences and how coroutines offer an advantage in asynchronous programming:",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Main Routine: The core part of a program where the entry point (e.g., main function) resides.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subroutines: Functions called within another function, creating a nested execution structure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Call Stack: A data structure that keeps track of currently running routines and their call history.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Blocking vs. Non-Blocking: Subroutine execution blocks the caller until it finishes, while coroutines can be non-blocking.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutines vs. Subroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines differ from subroutines in their non-blocking nature.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They are launched concurrently with the caller, allowing the main routine to continue execution without waiting for the coroutine to finish.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutines work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine starts running inside a thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If it encounters a long task (e.g., an API call), it pauses execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The idle time is used to run other tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once the paused task is ready, it resumes execution from where it left off.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines work by suspending and resuming execution at certain points in the code.  This is achieved using suspension points, which temporarily stop the coroutine’s execution without blocking the thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a coroutine is created, it’s initially in a suspended state. It will only start executing when it’s explicitly started using a coroutine builder, such as launch or async.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once the coroutine is running, it can suspend itself at any point using the suspend keyword. When a coroutine is suspended, it releases the thread it's running on, allowing other coroutines to run.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once the task is complete, the coroutine can resume execution and pick up where it left off.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Thread  Vs Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A thread is an independent execution path of a program. Every program has at least one thread. In Android, this is usually the main thread or UI thread. Threads help optimize CPU idle time. For example, if one thread is waiting for a network response, another thread can do some other work.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine runs inside a thread, and the dispatcher decides which thread it runs on. The important feature of coroutines is suspension. When a coroutine calls a suspend function, it can pause its execution and resume later. But this suspension does not block the thread. It only suspends the coroutine. That means multiple coroutines can run on the same thread. If one coroutine suspends, the thread can still be used by other coroutines. But if a coroutine runs blocking code, like Thread.sleep(), then it blocks the actual thread. If this happens on the main thread, the UI can freeze or drop frames.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread is heavy and OS-dependent, but Coroutine is lightweight and managed by Kotlin.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In fact, Thread is a simultaneous execution of tasks, but Coroutine is an asynchronous execution with Kotlin control.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In fact, Thread is a simultaneous execution of tasks, but Coroutine is an asynchronous execution with Kotlin control.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.*
                    import kotlin.time.Duration.Companion.seconds
                    import java.util.concurrent.ConcurrentHashMap

                    suspend fun main(){
                      // Thread safe set
                      val uniqueThreadNames = ConcurrentHashMap.newKeySet<Long>()
                      coroutineScope {
                        for (i in 1..1_000_000) {
                          launch {
                            delay(1.seconds)
                            uniqueThreadNames.add(Thread.currentThread().threadId())
                          }
                        }
                      }
                      println("Unique threads used: ${uniqueThreadNames.size}")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Cooperative Execution of Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can execute multiple coroutines (cooperative functions) on the same thread or different threads, depending on their availability.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If a thread is not busy, it executes another coroutine.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If a thread is busy, the coroutine scheduler asks the thread pool to provide a free thread.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Once the long-running task (e.g., API call) is complete, execution resumes from the exact suspended point, either on the same thread or a different one.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Coroutine Vs Thread",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

           Ul {
               Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Coroutines in Kotlin are a concurrency design pattern that enables asynchronous programming in a sequential manner. They provide a way to write asynchronous, non-blocking code that looks synchronous and is easier to understand and maintain. Coroutines allow developers to perform long-running tasks, such as network requests or disk I/O operations, without blocking the main thread or creating a large number of threads.",
                       modifier = Modifier.fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "One key difference between coroutines and threads is their resource usage. Threads are heavyweight operating system resources, and creating a new thread for each concurrent task can be resource-intensive and inefficient, especially on platforms with limited resources like mobile devices. Coroutines, on the other hand, are lightweight and can be launched by the hundreds or thousands without significant overhead.",
                       modifier = Modifier.fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Threads typically follow a preemptive multitasking model, where the operating system scheduler decides when to switch between threads. This can lead to unpredictable behavior and potential race conditions if not managed properly. Coroutines, on the other hand, follow a cooperative multitasking model, where suspension points are explicitly defined, and control is handed over between coroutines at those points. This allows for more predictable and deterministic behavior, as well as easier management of concurrency.",
                       modifier = Modifier.fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Coroutines may be thought of as lightweight threads. They are called so because multiple coroutines can be scheduled to be executed on the same thread.",
                       modifier = Modifier.fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "So, the creation of 100,000 threads results in the creation of 100,000 threads.",
                       modifier = Modifier.fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Whereas, the creation of 100,000 coroutines doesn’t necessarily mean that 100,000 threads get created.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Since the resources of a single thread are shared by multiple coroutines, they are much lighter when compared to creating raw threads.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Ul {
                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "A coroutine can be executed inside a thread.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "We can start multiple coroutines inside a single thread.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Coroutines are suspendable, that means, we can start and stop a coroutine whenever we can unlike threads.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Coroutines can switch context, that means, couroutines that started in one thread can switch to another thread easily.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Additionally, coroutines provide built-in support for structured concurrency, allowing developers to scope and manage coroutines in a hierarchical manner. This helps prevent resource leaks and ensures proper cleanup of resources when coroutines are cancelled or complete.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "In summary, while both coroutines and threads enable concurrent programming, coroutines offer a more lightweight, efficient, and predictable way to write asynchronous code in Kotlin. They provide a higher-level abstraction for concurrency management and are particularly well-suited for asynchronous tasks in Android development.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }
           }

            SpanText(
                text = "Thread.sleep() vs delay",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Section 1: Thread.sleep() is a blocking function that pauses the execution of the current thread for a specified amount of time. When used in a coroutine, it blocks the entire thread, preventing other coroutines from executing concurrently. This can lead to poor performance and unresponsiveness in your application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Blocking the main thread When using Thread.sleep() in the main thread, the entire application will be blocked, including the UI. This can lead to an unresponsive user interface.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                    fun main() {
                        println("Before sleep")
                        Thread.sleep(2000)
                        println("After sleep")
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Section 2: delay() in Kotlin Coroutines delay() is a suspending function provided by Kotlin Coroutines. It is designed to introduce a non-blocking delay in the execution of a coroutine. When a coroutine encounters a delay(), it suspends its execution without blocking the underlying thread. This allows other coroutines to continue executing concurrently, resulting in better performance and responsiveness.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Non-blocking delay with delay() Using delay() in a coroutine allows other coroutines to continue executing while the current coroutine is suspended. This ensures that the application remains responsive, even when introducing delays.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                    import kotlinx.coroutines.*

                    fun main() {
                        println("Before delay")
                        runBlocking {
                            delay(2000)
                        }
                        println("After delay")
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Section 3: Comparing Thread.sleep() and delay() In this section, we will compare the behavior of Thread.sleep() and delay() in different scenarios.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Performance and concurrency Thread.sleep() blocks the entire thread, preventing other coroutines from executing concurrently. On the other hand, delay() suspends the current coroutine without blocking the thread, allowing other coroutines to run concurrently. This improves performance and concurrency in your application.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Live data builder work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The coroutine only starts when the Live Data is active, meaning it should have at least one active observer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the Live data becomes inactive while the coroutine is executing, it will wait until [timeoutInMs] and if there’s still no observer, it will get cancelled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the live data becomes active again after cancellation, it will execute again. However if it was completed or even threw an exception then it would not be restarted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It runs in main thread by default but thread can be switched by using it like liveData(Dispatchers.IO) {..}",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "emit() can be called from within any thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What makes a coroutine cooperative?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are two ways to make non-cooperative coroutine cooperative.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Periodically call a suspending function that checks for cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Only those suspending functions that belong to kotlinx.coroutines package will make coroutine cooperative",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "delay(), yield(), withContext(), withTimeout() etc are the suspending functions that belongs to kotlinx.coroutines package.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutines Components",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(src = Resources.Images.COROUTINES_COMPONENTS,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Coroutines components")

            SpanText(
                text = "Coroutine Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine lifecycle is managed through Job objects, which track the state and manage the execution of coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Understanding the lifecycle of a coroutine helps in effectively managing and controlling coroutine execution",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "New: A coroutine has been created but has not started executing.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Active: A coroutine is currently executing.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Completing: The coroutine is finishing its execution, either normally or exceptionally.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancelled: The coroutine has been cancelled and is about to terminate.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Completed: The coroutine has finished execution successfully or with an exception.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            WebImage(src = Resources.Images.COROUTINES_LIFECYCLE_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Coroutines lifecycle")

            WebImage(src = Resources.Images.COROUTINES_LIFECYCLE_2,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Coroutines lifecycle")

            KotlinCode(
                code = $$"""
                    fun main() = runBlocking {
                        val job = launch {
                            try {
                                println("Coroutine is starting...")
                                delay(2000L)
                                println("Coroutine is working...")
                            } catch (e: CancellationException) {
                                println("Coroutine was cancelled")
                            } finally {
                                println("Coroutine is completing...")
                            }
                        }
                        println("Job state after launch:  ${job.isActive}") // true 
                        delay(1000L)
                        println("Cancelling job...")
                        job.cancelAndJoin()
                        println("Job state after cancel:  ${job.isCancelled}") // true
                        println("Job is completed:  ${job.isCompleted}") // true
                    }

                    // Output
                    Job state after launch: true
                    Coroutine is starting...
                    Cancelling job...
                    Coroutine was cancelled
                    Coroutine is completing...
                    Job state after cancel: true
                    Job is completed: true
                    """.trimIndent()
            )

            SpanText(
                text = "Advanced topics",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "suspend keyword: Marks functions that can be paused and resumed, enabling suspending operations within coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channels: Provide safe communication mechanisms between coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines Builders: Offer ways to manage and orchestrate the execution of coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Structured Concurrency
                    
                    //coroutineScope → Ensures structured concurrency: both async jobs (fetching details and posts) are tied to the same scope.
                    //async → Runs both operations concurrently.
                    //await() → Waits for both to finish before constructing the UserData.
                    
                    suspend fun fetchUser(key: String): UserData = coroutineScope {
                        val userId = getUserIdByKey(key)

                        val userDetails = async { fetchUserDetails(userId) }
                        val posts = async { fetchPosts(userId) }

                        UserData(
                            user = userDetails.await(),
                            posts = posts.await()
                        )
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@abdullahkhan1571998/kotlin-coroutines-with-callbacks-handling-asynchronous-tasks-and-cancellations-a4b52055d0a4